/** GROAPA subsolurilor, ca strat al machetei unificate (timelapse-ul).
 *
 *  O TĂIETURĂ SOLIDĂ în teren (D8, Stefan, 2.11.2026): placa terenului cu
 *  gaura gropii săpate până acum, pereții ei sprijiniți cu piloți, talpa, și
 *  radierul care urmează frontul săpăturii — nu norul de puncte pe straturi al
 *  Studioului. Frontul vine din organizarea de șantier (`sapaturi`: de la ce
 *  capăt și încotro), fracțiile din program (`stareaGropilor`).
 *
 *  Tot în cadrul documentului (`punctInCadru`), direct în `lume`; terenul
 *  machetei (placa și iarba lotului) se stinge cât timp stratul e aprins
 *  (`stareaSolului(…, groapa = true)`), ca gaura să se vadă. */
import * as THREE from "three";
import { punctInCadru } from "../canonical";
import type { CanonicalDoc } from "../canonical";
import { REALIST, materialRealist, muchiiFamilie } from "../../structure/realist3d";
import { concreteMaterial, type ConcreteState } from "../../structure/siteMaterials";
import type { OrganizareSantier } from "./santier";
import type { StratAtasat } from "../macheta/tipuri";

type Pt = [number, number];
type Obiect = { id?: string; type: string; properties?: Record<string, unknown> | null; geometry?: Record<string, unknown> | null };

const ADANCIME_RADIER = 0.85;
const M = {
  teren: new THREE.MeshStandardMaterial({ color: 0xa69e8e, roughness: 0.95 }),
  perete: new THREE.MeshStandardMaterial({ color: 0x8c7658, roughness: 1.0, side: THREE.BackSide }),
  talpa: new THREE.MeshStandardMaterial({ color: 0xa08a68, roughness: 1.0 }),
  piloti: new THREE.MeshStandardMaterial({ color: 0x9a9fa6, roughness: 0.8 }),
  radier: materialRealist("fundatie"),
};

function poligon(pts: Pt[], gauri: Pt[][] = []): THREE.Shape {
  const sh = new THREE.Shape();
  pts.forEach(([x, y], i) => (i ? sh.lineTo(x, y) : sh.moveTo(x, y)));
  sh.closePath();
  for (const g of gauri) sh.holes.push(new THREE.Path(g.map(([x, y]) => new THREE.Vector2(x, y))));
  return sh;
}
function arie(pts: Pt[]) {
  let s = 0;
  for (let i = 0; i < pts.length; i++) { const a = pts[i], b = pts[(i + 1) % pts.length]; s += a[0] * b[1] - b[0] * a[1]; }
  return Math.abs(s) / 2;
}
/** Poligonul tăiat cu semiplanul «la cel mult `dist` de `p0` pe direcția
 *  `dir`» — frontul săpăturii (Sutherland–Hodgman pe un semiplan). */
export function taieDirectie(pts: Pt[], p0: Pt, dir: Pt, dist: number): Pt[] {
  const s = (p: Pt) => (p[0] - p0[0]) * dir[0] + (p[1] - p0[1]) * dir[1] - dist;
  const out: Pt[] = [];
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i], b = pts[(i + 1) % pts.length]; const sa = s(a), sb = s(b);
    if (sa <= 0) out.push(a);
    if ((sa <= 0) !== (sb <= 0)) { const k = sa / (sa - sb); out.push([a[0] + (b[0] - a[0]) * k, a[1] + (b[1] - a[1]) * k]); }
  }
  return out;
}
function bbox(pts: Pt[]) {
  const xs = pts.map((p) => p[0]), ys = pts.map((p) => p[1]);
  return { x0: Math.min(...xs), x1: Math.max(...xs), y0: Math.min(...ys), y1: Math.max(...ys) };
}

export interface StareGroapa { sapat: number; radier: number }

export interface StratGroapa extends StratAtasat {
  /** aduce groapa la starea săptămânii: `{}` = nicio groapă deschisă (stratul se stinge);
   *  `beton` = the «Concrete» layer state the raft follows (solid on site, the
   *  Studio's translucent X-ray with its edges, or hidden) */
  aplica: (stari: Record<string, StareGroapa>, beton?: ConcreteState) => void;
  /** există subsoluri de săpat în document */
  areSubsoluri: boolean;
}

/** Stratul gropii: `sit` e documentul de situație BRUT (netransformat), `origine`
 *  originea locală a machetei, `zTeren` cota terenului (−ridicare), `hNivel`
 *  înălțimea unui nivel de subsol, `niveluri(cod)` câte niveluri are subsolul. */
export function construiesteGroapa(sit: CanonicalDoc, origine: [number, number], zTeren: number, hNivel: number,
                                   niveluri: (cod: string) => number, organizare: OrganizareSantier | null): StratGroapa {
  const pt = punctInCadru(sit.crs, origine);
  const P = (p: number[]): Pt => pt(p) as Pt;
  const poly = (o: Obiect): Pt[] => ((o.geometry?.poly as number[][]) ?? []).map(P);
  const O = (sit.objects ?? []) as Obiect[];
  const site = O.find((o) => o.type === "Site");
  const lot = site ? poly(site) : [];
  const subsoluri: Record<string, Pt[]> = {};
  for (const o of O) if (o.type === "Area" && o.properties?.kind === "basement") subsoluri[String(o.properties.code)] = poly(o);
  const grup = new THREE.Group();
  grup.name = "groapa";
  grup.visible = false;
  // the raft on site: floated concrete, opaque (the layer's own copy, disposed with it)
  const radierPlin = concreteMaterial("smooth");
  const b = lot.length ? bbox(lot) : { x0: -100, x1: 100, y0: -100, y1: 100 };
  const cx = (b.x0 + b.x1) / 2, cy = (b.y0 + b.y1) / 2;
  let deArs: { dispose(): void }[] = [];
  const goleste = () => { for (const d of deArs) d.dispose(); deArs = []; grup.clear(); };
  const adauga = (g: THREE.BufferGeometry, m: THREE.Material, umbra = true) => {
    const mesh = new THREE.Mesh(g, m); mesh.castShadow = umbra; mesh.receiveShadow = true;
    deArs.push(g); grup.add(mesh); return mesh;
  };
  // direcția săpăturii fiecărui subsol, din organizarea de șantier (un vector
  // al documentului trece prin aceeași transformare afină ca punctele)
  const directii: Record<string, { p0: Pt; dir: Pt }> = {};
  for (const sp of organizare?.sapaturi ?? []) {
    const p0 = P(sp.de_la), p1 = P([sp.de_la[0] + sp.spre[0] * 10, sp.de_la[1] + sp.spre[1] * 10]);
    const L = Math.hypot(p1[0] - p0[0], p1[1] - p0[1]) || 1;
    directii[sp.unitate] = { p0, dir: [(p1[0] - p0[0]) / L, (p1[1] - p0[1]) / L] };
  }
  const aplica = (stari: Record<string, StareGroapa>, beton: ConcreteState = "solid") => {
    goleste();
    const coduri = Object.keys(stari).filter((c) => subsoluri[c]);
    grup.visible = coduri.length > 0;
    if (!coduri.length) return;
    const gauri: Pt[][] = [];
    for (const cod of coduri) {
      const poligonS = subsoluri[cod];
      const st = stari[cod];
      const d = directii[cod];
      let zona: (fr: number) => Pt[];
      if (d) {
        const lung = Math.max(...poligonS.map((p) => (p[0] - d.p0[0]) * d.dir[0] + (p[1] - d.p0[1]) * d.dir[1]));
        zona = (fr) => (fr >= 1 ? poligonS : taieDirectie(poligonS, d.p0, d.dir, fr * lung));
      } else {
        // fără document: dinspre capătul mai departe de centrul lotului
        const bb = bbox(poligonS); const peX = bb.x1 - bb.x0 >= bb.y1 - bb.y0;
        const dir: Pt = peX ? [1, 0] : [0, 1]; const lo: Pt = peX ? [bb.x0, 0] : [0, bb.y0]; const hi: Pt = peX ? [bb.x1, 0] : [0, bb.y1];
        const dinspreLo = Math.abs((peX ? lo[0] : lo[1]) - (peX ? cx : cy)) > Math.abs((peX ? hi[0] : hi[1]) - (peX ? cx : cy));
        const p0: Pt = dinspreLo ? lo : hi; const dd: Pt = dinspreLo ? dir : [-dir[0], -dir[1]];
        const lung = peX ? bb.x1 - bb.x0 : bb.y1 - bb.y0;
        zona = (fr) => (fr >= 1 ? poligonS : taieDirectie(poligonS, p0, dd, fr * lung));
      }
      const sapat = zona(st.sapat);
      if (sapat.length < 3 || arie(sapat) < 5) continue;
      gauri.push(sapat);
      const zTalpa = zTeren - (niveluri(cod) || 2) * hNivel - ADANCIME_RADIER;
      // talpa, pereții (fețele dinăuntru), piloții pe contur
      const talpa = new THREE.ShapeGeometry(poligon(sapat)); talpa.translate(0, 0, zTalpa);
      adauga(talpa, M.talpa, false);
      const pereti = new THREE.ExtrudeGeometry(poligon(sapat), { depth: zTeren - zTalpa, bevelEnabled: false });
      pereti.translate(0, 0, zTalpa);
      adauga(pereti, M.perete, false);
      const piloti: THREE.BufferGeometry[] = [];
      for (let i = 0; i < sapat.length; i++) {
        const a = sapat[i], c = sapat[(i + 1) % sapat.length]; const L = Math.hypot(c[0] - a[0], c[1] - a[1]); const n = Math.floor(L / 1.2);
        for (let k = 0; k < n; k++) {
          const t = (k + 0.5) / n; const p = new THREE.CylinderGeometry(0.3, 0.3, zTeren - zTalpa, 8);
          p.rotateX(Math.PI / 2); p.translate(a[0] + (c[0] - a[0]) * t, a[1] + (c[1] - a[1]) * t, (zTeren + zTalpa) / 2); piloti.push(p);
        }
      }
      for (const p of piloti) adauga(p, M.piloti, false);
      // radierul, pe același front
      if (st.radier > 0 && beton !== "hidden") {
        const rad = zona(st.radier);
        if (rad.length >= 3 && arie(rad) > 5) {
          const g = new THREE.ExtrudeGeometry(poligon(rad), { depth: 0.7, bevelEnabled: false }); g.translate(0, 0, zTalpa);
          const m = adauga(g, beton === "solid" ? radierPlin : M.radier, true);
          if (beton !== "solid") { const mu = muchiiFamilie(m, "fundatie"); if (mu) { grup.add(mu); deArs.push(mu.geometry, mu.material as THREE.Material); } }
        }
      }
    }
    // terenul: o placă mare cu lotul decupat, și lotul cu gropile decupate
    const marg = 400;
    const placa = new THREE.ShapeGeometry(poligon([[b.x0 - marg, b.y0 - marg], [b.x1 + marg, b.y0 - marg], [b.x1 + marg, b.y1 + marg], [b.x0 - marg, b.y1 + marg]], lot.length ? [lot] : []));
    placa.translate(0, 0, zTeren - 0.05);
    adauga(placa, M.teren, false);
    if (lot.length) {
      const lotG = new THREE.ShapeGeometry(poligon(lot, gauri)); lotG.translate(0, 0, zTeren + 0.012);
      adauga(lotG, M.teren, false);
    }
  };
  return {
    id: "structura" as const, grup, areSubsoluri: Object.keys(subsoluri).length > 0,
    tinta: () => null, evidentiaza: () => undefined, aplica,
    dispose: () => { goleste(); radierPlin.dispose(); },
  } as StratGroapa;
}

export const CULOARE_RADIER = REALIST.fundatie;
